import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getApiConfig } from '../config';
import { getCurrentDomain } from '../utils/domainUtils';
import { tokenService } from '../services/tokenService';
import { Transaction, PaymentMethod } from '../types';

/**
 * API client configuration
 */
interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  headers: Record<string, string>;
}

/**
 * Create an Axios instance with the given configuration
 * @param config - API client configuration
 * @returns Axios instance
 */
const createAxiosInstance = (config: ApiClientConfig): AxiosInstance => {
  const instance = axios.create(config);

  // Request interceptor
  instance.interceptors.request.use(
    async (config) => {
      try {
        // Get tokens from token service
        console.log('API Client: Attempting to get tokens from tokenService');
        const accessToken = tokenService.getAccessToken();
        const idToken = tokenService.getIdToken();
        
        console.log('API Client: Access token retrieved:', accessToken ? 'Token exists' : 'No token found');
        console.log('API Client: ID token retrieved:', idToken ? 'Token exists' : 'No token found');
        
        // Add access token to Authorization header
        if (accessToken) {
          console.log('API Client: Adding Authorization header with Bearer token');
          config.headers.Authorization = `Bearer ${accessToken}`;
          
          // Log the actual token format (first few characters) for debugging
          if (typeof accessToken === 'string') {
            console.log(`API Client: Access token format check - starts with: ${accessToken.substring(0, 10)}...`);
          } else {
            console.warn('API Client: Access token is not a string:', typeof accessToken);
          }
        } else {
          console.warn('API Client: No access token available for request to:', config.url);
          
          // If this is a protected endpoint and we don't have a token, log more details
          if (config.url && !config.url.includes('/auth/')) {
            console.warn('API Client: This appears to be a protected endpoint, but no access token is available');
            console.log('API Client: Current auth state might need to be checked');
          }
        }
        
        // Add ID token to custom header
        if (idToken) {
          console.log('API Client: Adding ID token to X-Id-Token header');
          config.headers['X-Id-Token'] = idToken;
          
          // Log the actual token format (first few characters) for debugging
          if (typeof idToken === 'string') {
            console.log(`API Client: ID token format check - starts with: ${idToken.substring(0, 10)}...`);
          } else {
            console.warn('API Client: ID token is not a string:', typeof idToken);
          }
        } else {
          console.warn('API Client: No ID token available for request to:', config.url);
        }
        
        return config;
      } catch (error) {
        console.error('API Client: Error getting token:', error);
        return config;
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      console.log('API Error:', error.response?.status, error.config?.url);
      
      // Handle token expiration
      if (error.response && error.response.status === 401) {
        console.log('401 Unauthorized response detected');
        
        // Check if this is a getCurrentUser request - if so, we might be on page refresh
        const isAuthRequest = error.config?.url?.includes('/auth/me') ||
                             error.config?.url?.includes('/auth/current-user');
        
        // Check if this is a bank account request
        const isBankAccountRequest = error.config?.url?.includes('/bank-account');
        
        if (isAuthRequest) {
          console.log('Auth request failed - likely on page refresh');
          // For auth requests, we'll only remove the token if it's invalid
          // This helps prevent unnecessary logouts on transient errors
          
          // Check if we have a specific error indicating invalid token
          const isInvalidToken = error.response?.data?.message?.includes('invalid token') ||
                                error.response?.data?.message?.includes('expired token');
          
          if (isInvalidToken) {
            console.log('Invalid token detected, clearing tokens');
            tokenService.clearTokens();
            
            // Dispatch session expired event
            const sessionExpiredEvent = new CustomEvent('session-expired');
            window.dispatchEvent(sessionExpiredEvent);
          } else {
            console.log('Tokens might still be valid, keeping them for retry');
          }
        } else if (isBankAccountRequest) {
          console.log('Bank account request failed with 401, likely token issue');
          console.log('Clearing tokens and triggering re-authentication');
          tokenService.clearTokens();
          
          // Dispatch a custom event that the AuthContext can listen for
          const sessionExpiredEvent = new CustomEvent('session-expired');
          window.dispatchEvent(sessionExpiredEvent);
        } else {
          console.log('Non-auth request failed with 401, clearing tokens');
          tokenService.clearTokens();
          
          // Dispatch a custom event that the AuthContext can listen for
          const sessionExpiredEvent = new CustomEvent('session-expired');
          window.dispatchEvent(sessionExpiredEvent);
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * API client implementation
 */
class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor(config?: ApiClientConfig) {
    // Use provided config or get from configuration system
    const apiConfig = config || getApiConfig();
    this.axiosInstance = createAxiosInstance(apiConfig);
  }

  /**
   * Make a GET request
   * @param url - Request URL
   * @param config - Axios request configuration
   * @returns Promise that resolves with the response data
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.get(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Make a POST request
   * @param url - Request URL
   * @param data - Request data
   * @param config - Axios request configuration
   * @returns Promise that resolves with the response data
   */
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.post(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Make a PUT request
   * @param url - Request URL
   * @param data - Request data
   * @param config - Axios request configuration
   * @returns Promise that resolves with the response data
   */
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Make a DELETE request
   * @param url - Request URL
   * @param config - Axios request configuration
   * @returns Promise that resolves with the response data
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Handle API errors
   * @param error - Error object
   */
  private handleError(error: any): void {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      
      console.error(`API Error (${status}): ${message}`);
      
      // You can add more specific error handling here
      // For example, showing a toast notification or redirecting to an error page
    } else {
      console.error('Unexpected error:', error);
    }
  }
}

// Create a single instance of the API client
const apiClientInstance = new ApiClient();

/**
 * API client that provides methods for interacting with the API
 */
export const apiClient = {
  auth: {
    // These methods are now handled by Auth0
    // Keeping them for backward compatibility
    login: async (email: string, password: string) => {
      console.warn('Using deprecated login method. Auth0 is now handling authentication.');
      throw new Error('Login is now handled by Auth0. Please use the Auth0 login method instead.');
    },

    logout: async () => {
      console.warn('Using deprecated logout method. Auth0 is now handling authentication.');
      throw new Error('Logout is now handled by Auth0. Please use the Auth0 logout method instead.');
    },

    getCurrentUser: async () => {
      // This still needs to get user details from our API
      return apiClientInstance.get('/auth/me');
    },
  },
  
  disputes: {
    getAll: async () => {
      console.log('API Client: Fetching all disputes');
      try {
        const response = await apiClientInstance.get('/disputes');
        console.log('API Client: Disputes response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error fetching disputes:', error);
        // Return empty array on error to prevent UI crashes
        return [];
      }
    },
    
    getById: async (id: string) => {
      console.log(`API Client: Fetching dispute ${id}`);
      try {
        const response = await apiClientInstance.get(`/disputes/${id}`);
        console.log('API Client: Dispute response:', response);
        return response;
      } catch (error) {
        console.error(`API Client: Error fetching dispute ${id}:`, error);
        throw error;
      }
    },
    
    getByTransactionId: async (transactionId: string) => {
      console.log(`API Client: Fetching dispute for transaction ${transactionId}`);
      try {
        const response = await apiClientInstance.get(`/disputes/by-transaction/${transactionId}`);
        console.log('API Client: Dispute by transaction response:', response);
        return response;
      } catch (error) {
        console.error(`API Client: Error fetching dispute for transaction ${transactionId}:`, error);
        throw error;
      }
    },
    
    create: async (data: { transactionId: string; reason: string; message?: string }) => {
      console.log('API Client: Creating dispute');
      try {
        const response = await apiClientInstance.post('/disputes', data);
        console.log('API Client: Create dispute response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error creating dispute:', error);
        throw error;
      }
    },
    
    addMessage: async (disputeId: string, data: { message: string; visibility?: string }) => {
      console.log(`API Client: Adding message to dispute ${disputeId}`);
      try {
        // Convert visibility string to enum numeric value
        let visibilityValue = null;
        if (data.visibility) {
          switch (data.visibility) {
            case 'admin_only':
              visibilityValue = 0; // AdminOnly
              break;
            case 'buyer_only':
              visibilityValue = 1; // BuyerOnly
              break;
            case 'seller_only':
              visibilityValue = 2; // SellerOnly
              break;
            case 'both_parties':
              visibilityValue = 3; // BothParties
              break;
          }
        }
        
        // Format the request to match the backend's expected format
        const formattedData = {
            Message: data.message,
            Visibility: visibilityValue
        };
        
        console.log('API Client: Sending formatted data:', JSON.stringify(formattedData));
        const response = await apiClientInstance.post(`/disputes/${disputeId}/messages`, formattedData);
        console.log('API Client: Add message response:', response);
        return response;
      } catch (error) {
        console.error(`API Client: Error adding message to dispute ${disputeId}:`, error);
        throw error;
      }
    },
    
    resolve: async (disputeId: string, data: { resolution: string; notes?: string }) => {
      console.log(`API Client: Resolving dispute ${disputeId}`);
      try {
        const response = await apiClientInstance.post(`/disputes/${disputeId}/resolve`, data);
        console.log('API Client: Resolve dispute response:', response);
        return response;
      } catch (error) {
        console.error(`API Client: Error resolving dispute ${disputeId}:`, error);
        throw error;
      }
    },
    
    getStats: async () => {
      console.log('API Client: Fetching dispute statistics');
      try {
        const response = await apiClientInstance.get('/disputes/stats');
        console.log('API Client: Dispute statistics response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error fetching dispute statistics:', error);
        // Return default values on error to prevent UI crashes
        return {
          open: 0,
          underReview: 0,
          resolved: 0
        };
      }
    },
  },

  transactions: {
    getAll: async (page = 1, limit = 10): Promise<Transaction[]> => {
      console.log('API Client: Fetching transactions with page:', page, 'limit:', limit);
      try {
        // Get the raw response
        const rawResponse = await apiClientInstance.get(`/transactions?page=${page}&limit=${limit}`);
        console.log('API Client: Raw transactions response:', rawResponse);
        
        // Handle different response formats
        let transactions: any[] = [];
        
        if (Array.isArray(rawResponse)) {
          // Response is already an array
          transactions = rawResponse;
        } else if (rawResponse && typeof rawResponse === 'object') {
          // Type assertion for TypeScript
          const response = rawResponse as Record<string, any>;
          
          // Try to extract transactions array from common response formats
          if (response.transactions && Array.isArray(response.transactions)) {
            console.log('API Client: Extracted transactions array from response.transactions');
            transactions = response.transactions;
          } else if (response.data && Array.isArray(response.data)) {
            console.log('API Client: Extracted transactions array from response.data');
            transactions = response.data;
          } else if (response.items && Array.isArray(response.items)) {
            console.log('API Client: Extracted transactions array from response.items');
            transactions = response.items;
          } else if (response.results && Array.isArray(response.results)) {
            console.log('API Client: Extracted transactions array from response.results');
            transactions = response.results;
          } else {
            // Try to find any array property in the response
            const arrayProps = Object.keys(response).filter(key => Array.isArray(response[key]));
            if (arrayProps.length > 0) {
              console.log(`API Client: Found array property '${arrayProps[0]}' in response`);
              transactions = response[arrayProps[0]];
            }
          }
        }
        
        // Process each transaction to handle PaymentMethod
        const processedTransactions = transactions.map(transaction => {
          // Handle legacy format where paymentMethod is a string
          if (transaction && typeof transaction.paymentMethod === 'string' && transaction.paymentMethod) {
            console.log('API Client: Converting legacy paymentMethod string to object for transaction:', transaction.id);
            
            // Create a PaymentMethod object from the string value
            const paymentMethodObj: PaymentMethod = {
              id: transaction.paymentMethodId || 'unknown',
              paymentType: transaction.paymentMethod as string,
              paymentDate: transaction.createdAt || new Date().toISOString()
            };
            
            // Update the transaction with the new PaymentMethod object
            transaction.paymentMethod = paymentMethodObj;
          }
          
          return transaction;
        });
        
        return processedTransactions as Transaction[];
      } catch (error) {
        console.error('API Client: Error fetching transactions:', error);
        // Return empty array on error to prevent UI crashes
        return [];
      }
    },

    getById: async (id: string): Promise<Transaction> => {
      try {
        const response = await apiClientInstance.get<Transaction>(`/transactions/${id}`);
        const transaction = response as unknown as Transaction;
        
        // Handle legacy format where paymentMethod is a string
        if (transaction && typeof transaction.paymentMethod === 'string' && transaction.paymentMethod) {
          console.log('API Client: Converting legacy paymentMethod string to object');
          
          // Create a PaymentMethod object from the string value
          const paymentMethodObj: PaymentMethod = {
            id: transaction.paymentMethodId || 'unknown',
            paymentType: transaction.paymentMethod as string,
            paymentDate: transaction.createdAt || new Date().toISOString()
          };
          
          // Update the transaction with the new PaymentMethod object
          transaction.paymentMethod = paymentMethodObj;
        }
        
        // Ensure the transaction includes payment method data
        if (transaction && !transaction.paymentMethod && transaction.paymentMethodId) {
          console.log('API Client: Transaction has paymentMethodId but no payment method object');
          
          // Create a default PaymentMethod object
          transaction.paymentMethod = {
            id: transaction.paymentMethodId,
            paymentType: 'unknown',
            paymentDate: transaction.createdAt || new Date().toISOString()
          };
        }
        
        return transaction;
      } catch (error) {
        console.error('API Client: Error fetching transaction:', error);
        throw error;
      }
    },

    create: async (data: any): Promise<Transaction> => {
      try {
        // If data contains a string paymentMethod, convert it to a PaymentMethod object
        if (data && typeof data.paymentMethod === 'string') {
          console.log('API Client: Converting paymentMethod string to object for transaction creation');
          
          // Create a PaymentMethod object from the string value
          const paymentType = data.paymentMethod;
          
          // Remove the string paymentMethod from the data
          delete data.paymentMethod;
          
          // The backend will handle creating the PaymentMethod object
          // We just need to ensure we're sending the correct data format
          data.paymentType = paymentType;
        }
        
        const response = await apiClientInstance.post<Transaction>('/transactions', data);
        
        // Process the response to handle PaymentMethod
        const transaction = response as unknown as Transaction;
        
        // Handle legacy format where paymentMethod is a string
        if (transaction && typeof transaction.paymentMethod === 'string' && transaction.paymentMethod) {
          console.log('API Client: Converting legacy paymentMethod string to object for created transaction');
          
          // Create a PaymentMethod object from the string value
          const paymentMethodObj: PaymentMethod = {
            id: transaction.paymentMethodId || 'unknown',
            paymentType: transaction.paymentMethod as string,
            paymentDate: transaction.createdAt || new Date().toISOString()
          };
          
          // Update the transaction with the new PaymentMethod object
          transaction.paymentMethod = paymentMethodObj;
        }
        
        return transaction;
      } catch (error) {
        console.error('API Client: Error creating transaction:', error);
        throw error;
      }
    },

    update: async (id: string, data: any): Promise<Transaction> => {
      try {
        // If data contains a string paymentMethod, convert it to a PaymentMethod object
        if (data && typeof data.paymentMethod === 'string') {
          console.log('API Client: Converting paymentMethod string to object for transaction update');
          
          // Create a PaymentMethod object from the string value
          const paymentType = data.paymentMethod;
          
          // Remove the string paymentMethod from the data
          delete data.paymentMethod;
          
          // The backend will handle updating the PaymentMethod object
          // We just need to ensure we're sending the correct data format
          data.paymentType = paymentType;
        }
        
        const response = await apiClientInstance.put<Transaction>(`/transactions/${id}`, data);
        
        // Process the response to handle PaymentMethod
        const transaction = response as unknown as Transaction;
        
        // Handle legacy format where paymentMethod is a string
        if (transaction && typeof transaction.paymentMethod === 'string' && transaction.paymentMethod) {
          console.log('API Client: Converting legacy paymentMethod string to object for updated transaction');
          
          // Create a PaymentMethod object from the string value
          const paymentMethodObj: PaymentMethod = {
            id: transaction.paymentMethodId || 'unknown',
            paymentType: transaction.paymentMethod as string,
            paymentDate: transaction.createdAt || new Date().toISOString()
          };
          
          // Update the transaction with the new PaymentMethod object
          transaction.paymentMethod = paymentMethodObj;
        }
        
        return transaction;
      } catch (error) {
        console.error('API Client: Error updating transaction:', error);
        throw error;
      }
    },

    releasePayment: async (id: string, rating?: number, comment?: string) => {
      const requestBody = rating !== undefined ? { rating, comment } : undefined;
      return apiClientInstance.post(`/transactions/${id}/release`, requestBody);
    },
    
    getTransactionTotals: async () => {
      console.log('API Client: Fetching transaction totals');
      try {
        const response = await apiClientInstance.get('/transactions/totals');
        console.log('API Client: Transaction totals response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error fetching transaction totals:', error);
        // Return default values on error to prevent UI crashes
        return {
          totalPendingRelease: 0,
          totalWithdrawable: 0
        };
      }
    },
    
    withdrawFunds: async () => {
      console.log('API Client: Initiating funds withdrawal');
      try {
        const response = await apiClientInstance.post('/transactions/withdraw');
        console.log('API Client: Withdrawal response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error withdrawing funds:', error);
        throw error;
      }
    },
    
    getTransactionStatistics: async () => {
      console.log('API Client: Fetching transaction statistics');
      try {
        const response = await apiClientInstance.get('/transactions/statistics');
        console.log('API Client: Transaction statistics response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error fetching transaction statistics:', error);
        // Return default values on error to prevent UI crashes
        return {
          totalCompletedTransactions: 0,
          totalTransactionValue: 0,
          transactionTypeDistribution: []
        };
      }
    },
    
    getAdminTransactions: async (incompleteOnly = true, page = 1, limit = 10): Promise<Transaction[]> => {
      console.log('API Client: Fetching admin transactions with page:', page, 'limit:', limit, 'incompleteOnly:', incompleteOnly);
      try {
        // Get the raw response
        const rawResponse = await apiClientInstance.get(
          `/transactions/admin?incompleteOnly=${incompleteOnly}&page=${page}&limit=${limit}`
        );
        console.log('API Client: Raw admin transactions response:', rawResponse);
        
        // Handle different response formats
        let transactions: any[] = [];
        
        if (Array.isArray(rawResponse)) {
          // Response is already an array
          transactions = rawResponse;
        } else if (rawResponse && typeof rawResponse === 'object') {
          // Type assertion for TypeScript
          const response = rawResponse as Record<string, any>;
          
          // Try to extract transactions array from common response formats
          if (response.transactions && Array.isArray(response.transactions)) {
            console.log('API Client: Extracted transactions array from response.transactions');
            transactions = response.transactions;
          } else if (response.data && Array.isArray(response.data)) {
            console.log('API Client: Extracted transactions array from response.data');
            transactions = response.data;
          } else if (response.items && Array.isArray(response.items)) {
            console.log('API Client: Extracted transactions array from response.items');
            transactions = response.items;
          } else if (response.results && Array.isArray(response.results)) {
            console.log('API Client: Extracted transactions array from response.results');
            transactions = response.results;
          } else {
            // Try to find any array property in the response
            const arrayProps = Object.keys(response).filter(key => Array.isArray(response[key]));
            if (arrayProps.length > 0) {
              console.log(`API Client: Found array property '${arrayProps[0]}' in response`);
              transactions = response[arrayProps[0]];
            }
          }
        }
        
        // Process each transaction to handle PaymentMethod
        const processedTransactions = transactions.map(transaction => {
          // Handle legacy format where paymentMethod is a string
          if (transaction && typeof transaction.paymentMethod === 'string' && transaction.paymentMethod) {
            console.log('API Client: Converting legacy paymentMethod string to object for transaction:', transaction.id);
            
            // Create a PaymentMethod object from the string value
            const paymentMethodObj: PaymentMethod = {
              id: transaction.paymentMethodId || 'unknown',
              paymentType: transaction.paymentMethod as string,
              paymentDate: transaction.createdAt || new Date().toISOString()
            };
            
            // Update the transaction with the new PaymentMethod object
            transaction.paymentMethod = paymentMethodObj;
          }
          
          return transaction;
        });
        
        return processedTransactions as Transaction[];
      } catch (error) {
        console.error('API Client: Error fetching admin transactions:', error);
        // Return empty array on error to prevent UI crashes
        return [];
      }
    },
    
    processReleasedPayments: async () => {
      console.log('API Client: Processing released payments');
      try {
        const response = await apiClientInstance.post<{
          message: string;
          processedCount: number;
          failedCount: number;
        }>('/transactions/process-released-payments');
        console.log('API Client: Process released payments response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error processing released payments:', error);
        throw error;
      }
    },
  },

  tracking: {
    getById: async (id: string) => {
      return apiClientInstance.get(`/tracking/${id}`);
    },

    addTracking: async (transactionId: string, data: any) => {
      return apiClientInstance.post(`/transactions/${transactionId}/tracking`, data);
    },

    updateStatus: async (id: string, status: string) => {
      return apiClientInstance.put(`/tracking/${id}/status`, { status });
    },
    
    validateTrackingNumber: async (trackingNumber: string, carrier: string) => {
      return apiClientInstance.post<{valid: boolean, used: boolean, message: string}>('/tracking/validate', { trackingNumber, carrier });
    },
  },

  notifications: {
    getAll: async () => {
      console.log('API Client: Getting all notifications');
      try {
        const response = await apiClientInstance.get('/notifications');
        console.log('API Client: Notifications retrieved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error getting notifications:', error);
        throw error;
      }
    },

    markAsRead: async (id: string) => {
      console.log('API Client: Marking notification as read');
      try {
        const response = await apiClientInstance.put(`/notifications/${id}/read`);
        console.log('API Client: Notification marked as read successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error marking notification as read:', error);
        throw error;
      }
    },

    markAllAsRead: async () => {
      console.log('API Client: Marking all notifications as read');
      try {
        const response = await apiClientInstance.put('/notifications/read-all');
        console.log('API Client: All notifications marked as read successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error marking all notifications as read:', error);
        throw error;
      }
    },
    
    // Note: This endpoint doesn't exist in the API. Using the regular notifications endpoint instead.
    getHistory: async () => {
      console.log('API Client: Getting notification history');
      try {
        const response = await apiClientInstance.get('/notifications');
        console.log('API Client: Notification history retrieved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error getting notification history:', error);
        throw error;
      }
    },
    
    saveSubscription: async (subscription: PushSubscription) => {
      console.log('API Client: Saving push notification subscription');
      try {
        // The browser's PushSubscription object has a non-standard structure
        // We need to extract the keys using JSON serialization and type assertion
        const subscriptionJSON = subscription.toJSON();
        
        // Extract the required properties from the subscription JSON
        const payload = {
          endpoint: subscription.endpoint,
          p256dh: (subscriptionJSON as any).keys?.p256dh,
          auth: (subscriptionJSON as any).keys?.auth
        };
        
        const response = await apiClientInstance.post('/push-subscriptions', payload);
        console.log('API Client: Push subscription saved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error saving push subscription:', error);
        throw error;
      }
    },
    
    deleteSubscription: async (endpoint: string) => {
      console.log('API Client: Deleting push notification subscription');
      try {
        const response = await apiClientInstance.delete(`/push-subscriptions?endpoint=${encodeURIComponent(endpoint)}`);
        console.log('API Client: Push subscription deleted successfully');
        return response;
      } catch (error) {
        // Check if it's a 404 error, which means the subscription was already deleted
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.log('API Client: Subscription already deleted or not found');
          // Return a successful response to prevent error propagation
          return { data: { message: 'Subscription already deleted or not found' } };
        }
        console.error('API Client: Error deleting push subscription:', error);
        throw error;
      }
    },
    
    updatePreferences: async (preferences: {
      enabled: boolean;
      transactionUpdates: boolean;
      securityAlerts: boolean;
      accountUpdates: boolean;
      marketingMessages: boolean;
    }) => {
      console.log('API Client: Updating notification preferences');
      try {
        const response = await apiClientInstance.put('/notification-preferences', preferences);
        console.log('API Client: Notification preferences updated successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error updating notification preferences:', error);
        throw error;
      }
    },
    
    getPreferences: async () => {
      console.log('API Client: Getting notification preferences');
      try {
        const response = await apiClientInstance.get('/notification-preferences');
        console.log('API Client: Notification preferences retrieved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error getting notification preferences:', error);
        // Return default preferences on error to prevent UI crashes
        return {
          enabled: true,
          transactionUpdates: true,
          securityAlerts: true,
          accountUpdates: true,
          marketingMessages: false
        };
      }
    },
    
    getVapidPublicKey: async () => {
      console.log('API Client: Getting VAPID public key');
      try {
        const response = await apiClientInstance.get<{ publicKey: string }>('/push-subscriptions/vapid-public-key');
        console.log('API Client: VAPID public key retrieved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error getting VAPID public key:', error);
        throw error;
      }
    },
  },

  payments: {
    getAll: async () => {
      return apiClientInstance.get('/payments');
    },
    
    validatePayment: async (data: {
      amount: number;
      sellerId?: string;
      userId?: string;
      itemId: string;
    }) => {
      // Get the current domain for the success URL
      const currentDomain = getCurrentDomain();
      
      return apiClientInstance.post<{
        paymentUrl: string;
        reference: string;
      }>('/payments/validate', {
        ...data,
        successUrl: `${currentDomain}/payment-processing/{reference}`
      });
    },
    
    getPaymentStatus: async (reference: string) => {
      return apiClientInstance.get<{
        status: 'pending' | 'completed' | 'failed';
        transactionId?: string;
        amount?: number;
        sellerId?: string;
        userId?: string;
        itemDescription?: string;
        paymentMethod?: string;
        errorCode?: string;
        errorMessage?: string;
      }>(`/payments/status/${reference}`);
    },
    
    createPaymentIntent: async (data: {
      totalAmount: number;
      originalAmount: number;
      platformFee: number;
      publicUserId: string;
      sellerId: string;
      itemId: string;
      promotionCode?: string;
      originalPlatformFee?: number;
    }) => {
      return apiClientInstance.post<{
        clientSecret: string;
        reference: string;
        promotion?: {
          code: string;
          id: string;
          originalFee: number;
          discountedFee: number;
          discountAmount: number;
        };
      }>('/payments/create-payment-intent', data);
    },
    
    confirmPayment: async (data: {
      paymentIntentId: string;
    }) => {
      return apiClientInstance.post<{
        transactionId: string;
        status: string;
      }>('/payments/confirm-payment', data);
    },
    
    // processTokenPayment method removed as part of PinPayments to Stripe transition
  },

  promotions: {
    validatePromotion: async (data: {
      code: string;
      transactionAmount: number;
    }) => {
      console.log('API Client: Validating promotion code:', data.code);
      try {
        const response = await apiClientInstance.post('/promotions/validate', data);
        console.log('API Client: Promotion validation response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error validating promotion:', error);
        throw error;
      }
    },

    calculateDiscountedFee: async (data: {
      promotionCode?: string;
      originalAmount: number;
      originalFee: number;
    }) => {
      console.log('API Client: Calculating discounted fee');
      try {
        const response = await apiClientInstance.post('/promotions/calculate-fee', data);
        console.log('API Client: Fee calculation response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error calculating discounted fee:', error);
        throw error;
      }
    },

    getStandardFee: async (amount: number) => {
      console.log('API Client: Getting standard platform fee for amount:', amount);
      try {
        const response = await apiClientInstance.get(`/promotions/standard-fee/${amount}`);
        console.log('API Client: Standard fee response:', response);
        return response;
      } catch (error) {
        console.error('API Client: Error getting standard fee:', error);
        throw error;
      }
    },
  },

  users: {
    getById: async (id: string) => {
      return apiClientInstance.get(`/users/${id}`);
    },

    update: async (id: string, data: any) => {
      return apiClientInstance.put(`/users/${id}`, data);
    },
    
    setSellerId: async (sellerId: string) => {
      return apiClientInstance.post('/users/me/seller-id', { sellerId });
    },
    
    setUserId: async (userId: string) => {
      return apiClientInstance.post('/users/me/user-id', { userId });
    },
    
    getUserIdStatus: async () => {
      return apiClientInstance.get('/users/me/user-id-status');
    },
    
    validateUserId: async (userId: string) => {
      return apiClientInstance.get(`/users/validate-user-id/${userId}`);
    },
    
    saveBankAccountToken: async (token: string) => {
      return apiClientInstance.post('/users/me/bank-account', { token });
    },
    
    getBankAccountStatus: async () => {
      return apiClientInstance.get<{ hasBankAccount: boolean }>('/users/me/bank-account/status');
    }
  },
  
  ratings: {
    create: async (data: {
      userId: string;
      transactionId: string;
      value: number;
      comment?: string;
    }) => {
      return apiClientInstance.post('/ratings', data);
    },
    
    getSellerRatings: async (sellerId: string) => {
      return apiClientInstance.get(`/ratings/seller/${sellerId}`);
    },
    
    getUserRatings: async (userId: string) => {
      return apiClientInstance.get(`/ratings/user/${userId}`);
    }
  },

  stripe: {
    createAccountSession: async () => {
      console.log('API Client: Creating Stripe Account Session');
      try {
        const response = await apiClientInstance.post<{ clientSecret: string }>('/auth/account-session');
        console.log('API Client: Account Session created successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error creating Account Session:', error);
        throw error;
      }
    },
    
    addExternalAccount: async (token: string) => {
      console.log('API Client: Adding external account with token');
      try {
        const response = await apiClientInstance.post<{ success: boolean, account: any }>('/auth/add-external-account', { token });
        console.log('API Client: External account added successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error adding external account:', error);
        throw error;
      }
    },
    
    getBankAccountDetails: async () => {
      console.log('API Client: Getting bank account details');
      try {
        const response = await apiClientInstance.get<{
          hasBankAccount: boolean;
          bankAccounts: Array<{
            id: string;
            accountHolderName: string;
            accountHolderType: string;
            routingNumber: string;
            last4: string;
            bankName: string;
            country: string;
            currency: string;
            status: string;
          }>;
        }>('/auth/bank-account-details');
        console.log('API Client: Bank account details retrieved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error getting bank account details:', error);
        throw error;
      }
    },
    
    // New methods for Stripe Connect Embedded Onboarding
    createAccountOnboardingSession: async (returnUrl: string) => {
      console.log('API Client: Creating Stripe Account Onboarding Session');
      try {
        const response = await apiClientInstance.post<{ clientSecret: string }>(
          '/auth/account-onboarding-session',
          { returnUrl }
        );
        console.log('API Client: Onboarding Session created successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error creating Onboarding Session:', error);
        throw error;
      }
    },
    
    updateOnboardingStatus: async (status: 'Incomplete' | 'InProgress' | 'Complete' | 'Redo') => {
      console.log(`API Client: Updating onboarding status to ${status}`);
      try {
        // Import the OnboardingStatus enum and use it to convert string to numeric value
        const { OnboardingStatus } = await import('../utils/stripeUtils');
        
        // Convert string status to numeric enum value
        let statusValue: number;
        switch (status) {
          case 'Incomplete':
            statusValue = OnboardingStatus.Incomplete;
            break;
          case 'InProgress':
            statusValue = OnboardingStatus.InProgress;
            break;
          case 'Complete':
            statusValue = OnboardingStatus.Complete;
            break;
          case 'Redo':
            statusValue = OnboardingStatus.Redo;
            break;
          default:
            statusValue = OnboardingStatus.Incomplete; // Default to Incomplete
        }
        
        const response = await apiClientInstance.post<{ success: boolean }>(
          '/auth/update-onboarding-status',
          { status: statusValue }
        );
        console.log('API Client: Onboarding status updated successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error updating onboarding status:', error);
        throw error;
      }
    },
    
    getOnboardingStatus: async () => {
      console.log('API Client: Getting onboarding status');
      try {
        const response = await apiClientInstance.get<{
          status: string;
          startedAt: string | null;
        }>('/auth/onboarding-status');
        console.log('API Client: Onboarding status retrieved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error getting onboarding status:', error);
        throw error;
      }
    },
    
    getVerificationStatus: async () => {
      console.log('API Client: Getting verification status');
      try {
        const response = await apiClientInstance.get<{
          status: string;
          requirements: string[];
          lastChecked: string;
        }>('/auth/verification-status');
        console.log('API Client: Verification status retrieved successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error getting verification status:', error);
        throw error;
      }
    },
    
    checkAbandonedOnboarding: async () => {
      console.log('API Client: Checking for abandoned onboarding');
      try {
        const response = await apiClientInstance.get<{
          isAbandoned: boolean;
          status: string;
        }>('/auth/check-abandoned-onboarding');
        console.log('API Client: Abandoned onboarding check completed');
        return response;
      } catch (error) {
        console.error('API Client: Error checking abandoned onboarding:', error);
        throw error;
      }
    }
  },

  admin: {
    sendTestNotification: async (data: {
      userId: string;
      type: string;
      title: string;
      message: string;
      url?: string;
    }) => {
      console.log('API Client: Sending test notification');
      try {
        const response = await apiClientInstance.post('/admin/notifications/send-test', data);
        console.log('API Client: Test notification sent successfully');
        return response;
      } catch (error) {
        console.error('API Client: Error sending test notification:', error);
        throw error;
      }
    }
  },
};